import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { isAxiosError } from 'axios'
import { ArrowUpRight, CalendarDays, Check, CheckCircle, Copy, Loader2, Monitor } from 'lucide-react'
import { Button as AriaButton } from 'react-aria-components'
import { toast } from 'sonner'
import { Button } from '@/components/base/buttons'
import { Modal, ModalContent, ModalHeader, ModalBody, ModalTrigger } from '@/components/ui/Modal'
import { PhoneInput } from 'react-international-phone'
import type { ParsedCountry } from 'react-international-phone'
import 'react-international-phone/style.css'
import { useBreakpoint } from '@/hooks/use-breakpoint'
import { loadFacebookSDK, launchEmbeddedSignup } from '@/lib/facebook-sdk'
import { isSafari } from '@/lib/utils'
import {
  useWhatsAppStatus,
  useConnectWhatsAppNumber,
  useActivateRedirect,
  useDeactivateRedirect,
  useResendOtp,
  useResetOnboarding,
  useVerifyNumber,
  useRedirectStatus,
  reportSignupEvent,
} from '@/hooks/api/useWhatsAppSignup'
import type { TwilioSenderStatus } from '@/hooks/api/useWhatsAppSignup'
import { BusinessProfileForm } from './BusinessProfileForm'
import { VerificationRedirectBanner } from '../VerificationRedirectBanner'

const E164_REGEX = /^\+[1-9]\d{6,14}$/

function WhatsAppLogo({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 32 32" className={className} aria-hidden="true">
      <path
        fill="#25D366"
        d="M16 0C7.163 0 0 7.163 0 16c0 2.822.735 5.583 2.135 8.017L0 32l8.185-2.146A15.94 15.94 0 0 0 16 32c8.837 0 16-7.163 16-16S24.837 0 16 0Z"
      />
      <path
        fill="#fff"
        d="M23.472 19.339c-.365-.183-2.159-1.066-2.493-1.187-.334-.122-.577-.183-.82.183-.243.365-.94 1.186-1.152 1.43-.213.243-.426.274-.79.091-.365-.183-1.543-.569-2.938-1.812-1.086-.968-1.819-2.163-2.032-2.528-.213-.365-.023-.562.16-.744.164-.164.365-.426.548-.639.183-.213.243-.365.365-.608.122-.243.061-.456-.03-.639-.091-.183-.82-1.977-1.124-2.706-.296-.71-.596-.614-.82-.625-.213-.01-.456-.012-.699-.012-.243 0-.638.091-.972.456-.334.365-1.276 1.247-1.276 3.041 0 1.795 1.306 3.529 1.488 3.772.183.243 2.569 3.925 6.226 5.503.87.376 1.548.6 2.077.768.872.278 1.667.239 2.294.145.7-.104 2.159-.883 2.463-1.735.304-.852.304-1.583.213-1.735-.091-.152-.334-.243-.699-.426Z"
      />
    </svg>
  )
}

function MetaLogo({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 287 191" className={className} aria-hidden="true">
      <path
        fill="#0668E1"
        d="M31.06 126.4c0 11 2.42 19.44 5.58 24.55 4.15 6.7 10.34 9.54 16.65 9.54 8.14 0 15.58-2.02 29.92-21.85 11.49-15.9 25.03-38.22 34.14-52.2l15.42-23.7c10.72-16.47 23.13-34.79 37.36-47.21 11.62-10.14 24.14-15.77 36.75-15.77 21.16 0 41.32 12.26 56.75 35.28 16.9 25.2 25.1 56.94 25.1 89.7 0 19.47-3.85 33.79-10.37 45.1-6.31 10.94-18.6 21.86-39.28 21.86v-31.14c17.72 0 22.13-16.28 22.13-34.9 0-26.55-6.19-56-19.82-77.06-9.68-14.94-22.22-24.07-36.03-24.07-14.94 0-26.96 11.27-40.48 31.37-7.19 10.68-14.57 23.7-22.85 38.4l-9.1 16.11c-18.29 32.4-22.92 39.79-32.05 51.96-15.99 21.33-29.63 29.42-47.65 29.42-21.36 0-34.86-9.25-43.22-23.19C1.06 156.02 0 141.83 0 125.66l31.06.74Z"
      />
      <path
        fill="#0080FB"
        d="M24.49 37.3C38.8 15.23 59.46 0 83.15 0c13.72 0 27.36 4.06 41.61 15.7 15.59 12.72 32.21 33.68 52.95 68.24l7.44 12.4c17.95 29.9 28.16 45.29 34.14 52.55 7.7 9.32 13.09 12.1 20.1 12.1 17.72 0 22.13-16.28 22.13-34.9l27.4-.87c0 19.47-3.85 33.79-10.37 45.1-6.31 10.94-18.6 21.86-39.28 21.86-12.86 0-24.25-2.8-36.85-14.68-9.7-9.13-21.02-25.35-29.73-39.91l-25.94-43.32c-13.02-21.75-24.97-37.94-31.9-45.31-7.44-7.9-17-17.44-32.26-17.44-12.35 0-22.85 8.66-31.63 21.9l-26.47-16.13Z"
      />
      <path
        fill="#4E82EA"
        d="M82.6 31.4c-12.35 0-22.85 8.66-31.63 21.9-12.42 18.68-20.03 46.53-20.03 73.24 0 11 2.42 19.44 5.58 24.55L10.72 168.4C1.06 156.02 0 141.83 0 125.66c0-30.87 8.47-63.05 24.49-87.8C38.8 15.98 59.46.7 83.15.7l-.55 30.7Z"
      />
    </svg>
  )
}


function HelpLink({ href, label }: { href: string | undefined; label: string }) {
  return (
    <a
      href={href || '#'}
      target={href ? '_blank' : undefined}
      rel={href ? 'noopener noreferrer' : undefined}
      onClick={href ? undefined : (e) => e.preventDefault()}
      className="text-blue-700 underline-offset-2 hover:underline dark:text-blue-300"
    >
      {label}
    </a>
  )
}

/** Strip formatting so the backend receives strict E.164 (e.g. `+31612345678`). */
function toE164(phone: string): string {
  return phone.replace(/(?!^\+)\D/g, '')
}

// eslint-disable-next-line react-refresh/only-export-components -- exported for tests
export function validatePhoneNumber(
  e164: string,
  dialCode: string,
  countryIso2: string,
  bonnieNumberE164?: string | null,
): string | null {
  const national = e164.slice(1 + dialCode.length)
  if (!national) return 'Phone number is required'
  if (!E164_REGEX.test(e164)) return 'Invalid phone number'
  if (countryIso2 !== 'it' && national.startsWith('0')) {
    return `Remove the leading 0 after the country code`
  }
  if (bonnieNumberE164 && e164 === bonnieNumberE164) {
    return 'Use a different number than the Bonnie number'
  }
  return null
}

// eslint-disable-next-line react-refresh/only-export-components -- exported for tests
export function apiErrorMessage(err: unknown): string {
  if (isAxiosError(err)) {
    const detail = (err.response?.data as { detail?: unknown } | undefined)?.detail
    if (typeof detail === 'string') return detail
    if (Array.isArray(detail)) {
      const msg = (detail[0] as { msg?: unknown } | undefined)?.msg
      if (typeof msg === 'string') return msg
    }
  }
  return err instanceof Error ? err.message : 'Failed'
}

interface WhatsAppEmbeddedSignupProps {
  assistantId: number
}

export function WhatsAppEmbeddedSignup({ assistantId }: WhatsAppEmbeddedSignupProps) {
  const [sdkLoaded, setSdkLoaded] = useState(false)
  const [verificationError, setVerificationError] = useState<string | null>(null)
  const [phoneSubmitted, setPhoneSubmitted] = useState(false)
  const [verificationNumber, setVerificationNumber] = useState('')
  const [bonnieCopied, setBonnieCopied] = useState(false)
  const [connecting, setConnecting] = useState(false)
  const [verifying, setVerifying] = useState(false)
  const [otpCode, setOtpCode] = useState('')
  const [resendCooldownUntil, setResendCooldownUntil] = useState(0)
  const [resendNow, setResendNow] = useState(Date.now())
  const [senderStatusSnapshot, setSenderStatusSnapshot] = useState<TwilioSenderStatus | null>(null)

  const RETRY_STORAGE_KEY = `wa_retry_${assistantId}`
  const MAX_RETRIES = 3
  const RETRY_COOLDOWN_MS = 3 * 60 * 1000   // 3 min between attempts
  const LOCKOUT_MS = 60 * 60 * 1000          // 1 hr after exhausting retries

  const getRetryState = () => {
    try { return JSON.parse(localStorage.getItem(RETRY_STORAGE_KEY) ?? '{}') } catch { return {} }
  }
  const retryState = getRetryState()
  const retryCount: number = retryState.count ?? 0
  const lockedUntil: number = retryState.lockedUntil ?? 0
  const isRetryLocked = Date.now() < lockedUntil

  const recordRetry = () => {
    const next = retryCount + 1
    const locked = next >= MAX_RETRIES ? Date.now() + LOCKOUT_MS : 0
    localStorage.setItem(RETRY_STORAGE_KEY, JSON.stringify({ count: next, lockedUntil: locked }))
  }

  const { t } = useTranslation()
  const isDesktop = useBreakpoint('md')
  const { data: status, isLoading: statusLoading } = useWhatsAppStatus(assistantId)
  const { data: redirectStatus } = useRedirectStatus(assistantId)
  const connectWhatsAppNumber = useConnectWhatsAppNumber()
  const activateRedirect = useActivateRedirect()
  const deactivateRedirect = useDeactivateRedirect()
  const resendOtp = useResendOtp()
  const resetOnboarding = useResetOnboarding()
  const verifyNumber = useVerifyNumber()

  const [phoneValue, setPhoneValue] = useState('')
  const [country, setCountry] = useState<ParsedCountry>({ name: 'Netherlands', iso2: 'nl', dialCode: '31', format: undefined, priority: undefined, areaCodes: undefined })
  const verificationE164 = phoneValue.replace(/(?!^\+)\D/g, '')
  const nationalDigits = verificationE164.length > 3 ? verificationE164.slice(3) : ''

  const metaAppId = import.meta.env.VITE_META_APP_ID
  const metaConfigId = import.meta.env.VITE_META_CONFIG_ID
  const metaSolutionId = import.meta.env.VITE_META_SOLUTION_ID
  const walkthroughUrl = import.meta.env.VITE_WHATSAPP_WALKTHROUGH_URL as string | undefined
  const faqUrl = import.meta.env.VITE_WHATSAPP_FAQ_URL as string | undefined
  const calendlyUrl = import.meta.env.VITE_WHATSAPP_CALENDLY_URL as string | undefined

  const bonnieNumber = status?.phone_number ?? null
  const isConnected = status?.connected

  useEffect(() => {
    console.log('WhatsAppEmbeddedSignup mounted', { metaAppId, metaConfigId, metaSolutionId })
    if (metaAppId) {
      loadFacebookSDK(metaAppId).then(() => {
        console.log('Facebook SDK loaded')
        setSdkLoaded(true)
      })
    }
  }, [metaAppId, metaConfigId, metaSolutionId])

  useEffect(() => {
    if (resendCooldownUntil <= 0) return
    const tick = setInterval(() => setResendNow(Date.now()), 1000)
    return () => clearInterval(tick)
  }, [resendCooldownUntil])

  // Fix #17: clear verifying state once connected
  useEffect(() => {
    if (status?.connected) setVerifying(false)
  }, [status?.connected])

  const handleConnect = useCallback(async () => {
    if (!sdkLoaded || !metaConfigId) {
      toast.error('Facebook SDK not loaded')
      return
    }

    const error = validatePhoneNumber(verificationE164, country.dialCode, country.iso2, status?.phone_number)
    if (error) {
      setVerificationError(error)
      return
    }

    setConnecting(true)
    reportSignupEvent({ assistant_id: assistantId, event: 'START', data: {} })
    try {
      await activateRedirect.mutateAsync({
        assistant_id: assistantId,
        redirect_phone_number: verificationE164,
      })

      let result
      try {
        result = await launchEmbeddedSignup(
          metaConfigId,
          metaSolutionId,
          undefined,
          undefined,
          (event, data) => reportSignupEvent({ assistant_id: assistantId, event, data }),
        )
      } catch (err) {
        await deactivateRedirect.mutateAsync({ assistant_id: assistantId })
        throw err
      }

      // Do NOT deactivate the redirect on success — Meta places the verification
      // call several seconds-to-minutes AFTER the popup closes. Let the backend
      // TTL expire it naturally, or the user cancel manually via the banner.
      await connectWhatsAppNumber.mutateAsync({
        assistant_id: assistantId,
        waba_id: result.wabaId,
        phone_number_id: result.phoneNumberId,
      })

      toast.success('WhatsApp number added to Bonnie')
    } catch (err) {
      const message = apiErrorMessage(err)
      if (message.includes('cancelled')) {
        toast.error('Signup cancelled')
      } else {
        toast.error(`Connection failed: ${message}`)
      }
    } finally {
      setConnecting(false)
    }
  }, [
    sdkLoaded,
    metaConfigId,
    metaSolutionId,
    verificationE164,
    country,
    status?.phone_number,
    assistantId,
    connectWhatsAppNumber,
    activateRedirect,
    deactivateRedirect,
  ])

  const resendSecondsLeft = Math.max(0, Math.ceil((resendCooldownUntil - resendNow) / 1000))

  const sideCard = (
    <div className="space-y-4">
      {/* Tutorial video card */}
      <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-900">
        <a href={walkthroughUrl || '#'} target={walkthroughUrl ? '_blank' : undefined} rel="noopener noreferrer" className="flex cursor-pointer items-start gap-4">
          <span className="flex-shrink-0">
            <svg width="80" height="64" viewBox="0 0 80 64" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M10 0.5H70C75.2467 0.5 79.5 4.7533 79.5 10V54C79.5 59.2467 75.2467 63.5 70 63.5H10C4.7533 63.5 0.5 59.2467 0.5 54V10C0.5 4.7533 4.7533 0.5 10 0.5Z" fill="url(#thumb_gradient)"/>
              <path d="M10 0.5H70C75.2467 0.5 79.5 4.7533 79.5 10V54C79.5 59.2467 75.2467 63.5 70 63.5H10C4.7533 63.5 0.5 59.2467 0.5 54V10C0.5 4.7533 4.7533 0.5 10 0.5Z" stroke="#EBEBEA"/>
              <path d="M37.1978 35.65V27.92c0-.37.397-.6.7-.4l5.86 3.86c.28.19.28.62 0 .8l-5.86 3.86c-.303.2-.7-.027-.7-.4Z" fill="white"/>
              <defs>
                <linearGradient id="thumb_gradient" x1="40" y1="0" x2="40" y2="64" gradientUnits="userSpaceOnUse">
                  <stop stopColor="#C569CF"/>
                  <stop offset="1" stopColor="#EE609C"/>
                </linearGradient>
              </defs>
            </svg>
          </span>
          <div>
            <p className="text-base font-semibold text-gray-900 dark:text-gray-100">Watch the setup tutorial</p>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">2min to avoid surprises.</p>
          </div>
        </a>
      </div>

      {/* Help links card */}
      <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-900">
        <div className="flex flex-col gap-5">
          <ModalTrigger>
            <AriaButton className="flex cursor-pointer items-center gap-3.5 text-sm font-medium text-gray-600 outline-none hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100">
              <svg className="h-5 w-5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18h6M10 21h4"/><path d="M12 3a6 6 0 00-4 10.5c.6.5 1 1.5 1 2.5h6c0-1 .4-2 1-2.5A6 6 0 0012 3z"/></svg>
              How does this work?
            </AriaButton>
            <Modal size="lg">
              <ModalContent>
                {({ close }) => (
                  <>
                    <ModalHeader onClose={close}>How does this work?</ModalHeader>
                    <ModalBody>
                      <p className="text-sm italic text-gray-600 dark:text-gray-400">
                        Through this flow you grant Bonnie access to add the assistant&apos;s phone number
                        as a WhatsApp Business number under your existing or new WhatsApp Business account.
                      </p>
                      <ol className="mt-4 space-y-3 text-sm text-gray-700 dark:text-gray-300">
                        {[
                          'Enter your personal phone number. Meta will call it to share a verification code.',
                          'Copy the Bonnie assistant number — you\'ll paste it in the Meta popup.',
                          'Click Connect, sign in to Meta, pick an existing or new WhatsApp Business Account.',
                          'When asked for a phone number, paste the Bonnie number you copied in step 2.',
                          'Choose "Verify by phone call".',
                          'Meta calls Bonnie, we forward it to you, you enter the code in the Meta popup.',
                          'After closing the popup, we connect everything — takes a few seconds.',
                          'Done. Test Bonnie on WhatsApp.',
                        ].map((step, i) => (
                          <li key={i} className="flex gap-3">
                            <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-gray-900 text-xs font-medium text-white dark:bg-gray-100 dark:text-gray-900">
                              {i + 1}
                            </span>
                            <span>{step}</span>
                          </li>
                        ))}
                      </ol>
                    </ModalBody>
                  </>
                )}
              </ModalContent>
            </Modal>
          </ModalTrigger>
          <a href={faqUrl || '#'} target={faqUrl ? '_blank' : undefined} rel="noopener noreferrer" className="flex items-center gap-3.5 text-sm font-medium text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100">
            <svg className="h-5 w-5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a8 8 0 01-8 8H4l2-3a8 8 0 1115-5z"/><path d="M10.5 10a1.8 1.8 0 013.5.6c0 1.2-1.8 1.6-1.8 2.6"/><circle cx="12.2" cy="16" r=".4" fill="currentColor"/></svg>
            Read the FAQ
          </a>
        </div>
      </div>

      {/* Getting stuck card */}
      <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-900">
        <div className="flex items-center gap-3">
          <CalendarDays className="h-5 w-5 flex-none text-gray-500" />
          <p className="text-base font-semibold text-gray-900 dark:text-gray-100">Getting stuck?</p>
        </div>
        <p className="mt-3 text-sm leading-relaxed text-gray-500 dark:text-gray-400">
          Schedule a short meeting with our customer success team. They&apos;ll help you out.
        </p>
        {calendlyUrl && (
          <a href={calendlyUrl} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100 hover:underline">
            Schedule a meeting
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
          </a>
        )}
      </div>
    </div>
  )

  const redirectBanner = <VerificationRedirectBanner assistantId={assistantId} showCancel />

  if (statusLoading) {
    return (
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="h-64 animate-pulse rounded-lg border border-gray-200 bg-gray-50/50 dark:border-gray-700 dark:bg-gray-900/20" />
        <div className="h-64 animate-pulse rounded-lg border border-gray-200 bg-gray-50/50 dark:border-gray-700 dark:bg-gray-900/20" />
      </div>
    )
  }

  if (isConnected && status) {
    // ponytail: technical fields logged for dev inspection only; not shown in UI.
    console.debug('[whatsapp status]', assistantId, status)
    return (
      <>
      {redirectBanner}
      <div className="grid gap-6 lg:grid-cols-2">
      <div className="space-y-4">
        <div className="rounded-lg border border-green-100 bg-green-50/50 p-4 dark:border-green-800/50 dark:bg-green-900/10">
          <div className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4 flex-shrink-0 text-green-600" />
            <p className="text-sm text-gray-900 dark:text-gray-100">This Bonnie is connected to WhatsApp</p>
          </div>
          {status.phone_number && (
            <div className="mt-3">
              <Button
                type="button"
                color="primary"
                size="sm"
                className="bg-[#222222] hover:bg-[#333333] dark:bg-gray-100 dark:hover:bg-white"
                onPress={() =>
                  window.open(
                    `https://wa.me/${status.phone_number!.replace('+', '')}`,
                    '_blank',
                    'noopener,noreferrer',
                  )
                }
              >
                Send Bonnie a WhatsApp
              </Button>
            </div>
          )}
        </div>
        <div className="rounded-lg border border-gray-200 bg-white/60 p-4 dark:border-gray-700 dark:bg-gray-900/30">
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Add a WhatsApp widget to your site</p>
          <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
            Configure an embeddable widget pointing at{' '}
            {status.phone_number && (
              <span className="font-mono">{status.phone_number}</span>
            )}
            {' '}so visitors can start a WhatsApp chat with Bonnie in one click. {t('resources.widgets.whatsapp_signup.managed_in_hub')}
          </p>
          <div className="mt-3">
            <Button
              color="primary"
              size="sm"
              className="bg-[#222222] hover:bg-[#333333] dark:bg-gray-100 dark:hover:bg-white"
              href="/widgets"
            >
              {t('resources.widgets.whatsapp_signup.open_hub')}
            </Button>
          </div>
        </div>
      </div>
      <div className="order-first lg:order-none">{sideCard}</div>
      </div>
      <BusinessProfileForm assistantId={assistantId} />
      </>
    )
  }

  if (status && !status.connected && (status.awaiting_otp || status.sender_offline || status.sender_added || status.can_programmatic_retry || resendOtp.isPending)) {
    const isOffline = !!status.sender_offline

    if (status.can_programmatic_retry) {
      return (
        <>
        {redirectBanner}
        <div className="grid gap-6 lg:grid-cols-2">
          <div className="self-start rounded-lg border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-900">
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
              Connection failed
            </h3>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              Something went wrong during WhatsApp verification. Click below to retry — we&apos;ll handle it automatically.
            </p>
            {senderStatusSnapshot?.blocked_reason || isRetryLocked ? (
              <div className="mt-3">
                <p className="text-sm text-red-600 dark:text-red-400">
                  {senderStatusSnapshot?.blocked_reason
                    ? (senderStatusSnapshot.remediation ?? 'Retry not possible — please contact support.')
                    : 'Too many attempts. Please wait an hour or schedule a call with our team.'}
                </p>
                {isRetryLocked && calendlyUrl && (
                  <a href={calendlyUrl} target="_blank" rel="noopener noreferrer"
                    className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-gray-900 hover:underline dark:text-gray-100">
                    Schedule a meeting →
                  </a>
                )}
              </div>
            ) : (
              <div className="mt-4">
                <Button
                  type="button"
                  color="primary"
                  size="sm"
                  className="bg-[#222222] hover:bg-[#333333] dark:bg-gray-100 dark:hover:bg-white"
                  isLoading={resendOtp.isPending}
                  isDisabled={resendSecondsLeft > 0 || resendOtp.isPending}
                  onPress={async () => {
                    try {
                      const snap = await resendOtp.mutateAsync({ assistant_id: assistantId })
                      setSenderStatusSnapshot(snap)
                      if (!snap.blocked_reason) {
                        recordRetry()
                        setResendCooldownUntil(Date.now() + RETRY_COOLDOWN_MS)
                        setResendNow(Date.now())
                        toast.success('Retry started — connecting your WhatsApp number')
                      }
                    } catch (e) {
                      toast.error(`Retry failed: ${apiErrorMessage(e)}`)
                    }
                  }}
                >
                  {resendSecondsLeft > 0 ? `Retry connection (${resendSecondsLeft}s)` : 'Retry connection'}
                </Button>
                {retryCount > 0 && (
                  <p className="mt-2 text-xs text-gray-400">{MAX_RETRIES - retryCount} attempt{MAX_RETRIES - retryCount !== 1 ? 's' : ''} remaining</p>
                )}
              </div>
            )}
            <div className="mt-3 border-t border-gray-100 pt-3 dark:border-gray-800">
              <button
                type="button"
                className="text-xs text-gray-400 hover:text-red-500 dark:text-gray-500 dark:hover:text-red-400"
                onClick={async () => {
                  try {
                    await resetOnboarding.mutateAsync({ assistant_id: assistantId })
                    toast.success('WhatsApp status reset')
                  } catch (e) {
                    toast.error(`Reset failed: ${apiErrorMessage(e)}`)
                  }
                }}
              >
                Reset WhatsApp status
              </button>
            </div>
          </div>
          <div className="order-first lg:order-none">{sideCard}</div>
        </div>
        </>
      )
    }

    const steps: Array<[string, boolean]> = [
      ['Registering your number with Meta', status.sender_added],
      ['Attaching to your Messaging Service', status.attached_to_messaging_service],
      ['Verifying with WhatsApp', status.sender_online],
    ]

    // Fix #17: show optimistic connecting spinner after OTP success
    if (verifying && !status.connected) {
      return (
        <>
        {redirectBanner}
        <div className="max-w-xl">
          <div className="flex items-center gap-3 rounded-lg border border-blue-100 bg-blue-50/40 p-6 dark:border-blue-900/40 dark:bg-blue-950/20">
            <Loader2 className="h-5 w-5 flex-shrink-0 animate-spin text-blue-600 dark:text-blue-400" />
            <p className="text-base font-medium text-gray-900 dark:text-gray-100">Connecting…</p>
          </div>
        </div>
        </>
      )
    }

    return (
      <>
      {redirectBanner}
      <div className="max-w-xl">
        {isOffline ? (
          <div className="space-y-4">
            <div className="rounded-lg border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-900">
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                Verification didn&apos;t complete
              </h3>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                Enter your personal phone number below. Meta will place a verification call and we&apos;ll forward it to you.
              </p>

              <div className="mt-4">
                <label htmlFor="wa-retry-number" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Personal phone number
                </label>
                {/* Fix #15: pre-fill from verificationNumber state or redirect_target fallback */}
                <input
                  id="wa-retry-number"
                  type="tel"
                  autoComplete="tel"
                  defaultValue={verificationNumber || redirectStatus?.redirect_target || ''}
                  placeholder="+31 6XXXXXXXX"
                  onChange={(e) => setVerificationNumber(e.target.value)}
                  style={{ outline: 'none' }}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800"
                />
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                <Button
                  type="button"
                  color="primary"
                  size="sm"
                  className="bg-[#222222] hover:bg-[#333333] dark:bg-gray-100 dark:hover:bg-white"
                  isDisabled={resendSecondsLeft > 0 || resendOtp.isPending || !!senderStatusSnapshot?.blocked_reason}
                  isLoading={resendOtp.isPending}
                  onPress={async () => {
                    const e164 = toE164(verificationNumber)
                    try {
                      const snap = await resendOtp.mutateAsync({
                        assistant_id: assistantId,
                        redirect_phone_number: e164 || undefined,
                      })
                      setSenderStatusSnapshot(snap)
                      // Fix #21: only set cooldown when not blocked
                      if (!snap.blocked_reason) {
                        setResendCooldownUntil(Date.now() + 60_000)
                        setResendNow(Date.now())
                        toast.success('Verification call requested — Meta will call you shortly')
                      } else {
                        toast.error(snap.remediation ?? 'Retry not possible — please contact support')
                      }
                    } catch (e) {
                      toast.error(`Retry failed: ${apiErrorMessage(e)}`)
                    }
                  }}
                >
                  {resendSecondsLeft > 0 ? `Request verification call (${resendSecondsLeft}s)` : 'Request verification call'}
                </Button>
              </div>

              {senderStatusSnapshot?.blocked_reason && (
                <p className="mt-3 text-sm text-red-600 dark:text-red-400">
                  {senderStatusSnapshot.remediation ?? 'Verification cannot be retried. Please contact support.'}
                </p>
              )}
            </div>

            <div className="rounded-lg border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-900">
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                Already received a code?
              </h3>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                Enter the 6-digit code Meta read out on the verification call.
              </p>
              <div className="mt-4 flex items-center gap-2">
                <input
                  id="wa-otp-resume"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="123456"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                  className="w-32 rounded-md border border-gray-300 px-3 py-2 text-center font-mono text-sm dark:border-gray-600 dark:bg-gray-800"
                />
                <Button
                  type="button"
                  color="primary"
                  size="sm"
                  className="bg-[#222222] hover:bg-[#333333] dark:bg-gray-100 dark:hover:bg-white"
                  isDisabled={otpCode.length < 6}
                  isLoading={verifyNumber.isPending}
                  onPress={async () => {
                    try {
                      await verifyNumber.mutateAsync({ assistant_id: assistantId, code: otpCode })
                      toast.success('Verified — connecting your WhatsApp number')
                      setOtpCode('')
                      setVerifying(true)
                    } catch (e) {
                      toast.error(`Verification failed: ${apiErrorMessage(e)}`)
                    }
                  }}
                >
                  Submit code
                </Button>
              </div>
            </div>
            <div className="mt-3 border-t border-gray-100 pt-3 dark:border-gray-800">
              <button
                type="button"
                className="text-xs text-gray-400 hover:text-red-500 dark:text-gray-500 dark:hover:text-red-400"
                onClick={async () => {
                  try {
                    await resetOnboarding.mutateAsync({ assistant_id: assistantId })
                    toast.success('WhatsApp status reset')
                  } catch (e) {
                    toast.error(`Reset failed: ${apiErrorMessage(e)}`)
                  }
                }}
              >
                Reset WhatsApp status
              </button>
            </div>
          </div>
        ) : (
          <div className="rounded-lg border border-blue-100 bg-blue-50/40 p-6 dark:border-blue-900/40 dark:bg-blue-950/20">
            <div className="flex items-center gap-3">
              <Loader2 className="h-5 w-5 flex-shrink-0 animate-spin text-blue-600 dark:text-blue-400" />
              <p className="text-base font-medium text-gray-900 dark:text-gray-100">
                We&apos;re connecting all the dots for you
              </p>
            </div>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              This usually takes a minute or two. Sit tight — no action needed on your side.
            </p>
            <ul className="mt-5 space-y-2.5 text-sm">
              {steps.map(([label, done]) => (
                <li key={label} className="flex items-center gap-2.5">
                  {done ? (
                    <Check className="h-4 w-4 flex-shrink-0 text-green-600 dark:text-green-400" />
                  ) : (
                    <Loader2 className="h-4 w-4 flex-shrink-0 animate-spin text-gray-400 dark:text-gray-500" />
                  )}
                  <span className={done ? 'text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400'}>
                    {label}
                  </span>
                </li>
              ))}
            </ul>
            {status.awaiting_otp && (
              <div className="mt-5 border-t border-blue-100 pt-4 dark:border-blue-900/40">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Received a verification code by phone?
                </p>
                <div className="mt-2 flex items-center gap-2">
                  <input
                    id="wa-otp-inline"
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="123456"
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                    className="w-32 rounded-md border border-blue-200 bg-white px-3 py-2 text-center font-mono text-sm dark:border-blue-800/50 dark:bg-gray-800"
                  />
                  <Button
                    type="button"
                    color="primary"
                    size="sm"
                    className="bg-[#222222] hover:bg-[#333333] dark:bg-gray-100 dark:hover:bg-white"
                    isDisabled={otpCode.length < 6}
                    isLoading={verifyNumber.isPending}
                    onPress={async () => {
                      try {
                        await verifyNumber.mutateAsync({ assistant_id: assistantId, code: otpCode })
                        toast.success('Verified — connecting your WhatsApp number')
                        setOtpCode('')
                        setVerifying(true)
                      } catch (e) {
                        toast.error(`Verification failed: ${apiErrorMessage(e)}`)
                      }
                    }}
                  >
                    Submit code
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      </>
    )
  }

  const copyBonnieNumber = async () => {
    if (!status?.phone_number) return
    try {
      await navigator.clipboard.writeText(status.phone_number)
      setBonnieCopied(true)
      toast.success('Bonnie number copied')
    } catch {
      toast.error('Copy failed')
    }
  }

  const submitPhone = () => {
    const err = validatePhoneNumber(verificationE164, country.dialCode, country.iso2, status?.phone_number)
    if (err) {
      setVerificationError(err)
      return
    }
    setVerificationError(null)
    setPhoneSubmitted(true)
  }

  return (
    <>
    {redirectBanner}
    <div className="grid grid-cols-1 items-start gap-10 lg:grid-cols-[minmax(0,600px)_320px]">
    <div>
      <div className="flex items-center gap-2">
        <WhatsAppLogo className="h-8 w-8" />
        <span className="text-gray-300 dark:text-gray-600">×</span>
        <MetaLogo className="h-6 w-auto" />
      </div>
      <h2 className="mt-4 text-2xl font-semibold tracking-tight text-gray-900 dark:text-gray-100">
        Enable WhatsApp Business
      </h2>
      <p className="mt-1.5 text-sm text-gray-500 dark:text-gray-400">
        Set up Bonnie on WhatsApp in minutes.
      </p>

      {/* Mobile warning */}
      <div className="mt-5 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 md:hidden dark:border-amber-800/50 dark:bg-amber-900/10">
        <Monitor className="mt-0.5 h-4 w-4 flex-none text-amber-600" />
        <p className="text-sm text-gray-800 dark:text-gray-200">
          This setup opens a Facebook popup that works poorly on mobile. Please use a laptop.
        </p>
      </div>

      {/* Safari warning */}
      {isDesktop && isSafari() && (
        <div className="mt-5 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-800/50 dark:bg-amber-900/10">
          <Monitor className="mt-0.5 h-4 w-4 flex-none text-amber-600" />
          <p className="text-sm text-gray-800 dark:text-gray-200">
            We noticed you&apos;re using Safari, which blocks the Meta signup popup by default.
            To allow it: <strong>Safari → Settings → Websites → Pop-up Windows → Allow</strong>.
          </p>
        </div>
      )}

      {/* Progressive two-step connect flow */}
      <div className="mt-6 hidden space-y-4 md:block">
        {/* Step 1: phone input */}
        <div className="overflow-visible rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-900">
          <p className="text-base font-semibold text-gray-900 dark:text-gray-100">Type your personal phone number</p>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Bonnie will redirect the verification call to this phone number.</p>
          <p className="mt-4 text-sm font-medium text-gray-700 dark:text-gray-300">Personal phone number</p>
          <PhoneInput
            defaultCountry="nl"
            preferredCountries={['nl', 'be', 'de']}
            value={phoneValue}
            disabled={phoneSubmitted}
            onChange={(val, meta) => {
              setPhoneValue(val)
              setCountry(meta.country)
              setVerificationError(null)
            }}
            inputProps={{ id: 'wa-verification-number', autoComplete: 'tel' }}
            style={{
              width: '100%',
              marginTop: '6px',
              border: verificationError ? '1px solid #f87171' : '1px solid #d0d5dd',
              borderRadius: '8px',
              boxShadow: '0 1px 2px rgba(16,24,40,.04)',
              background: '#fff',
              height: '44px',
            }}
            inputStyle={{
              border: 'none',
              outline: 'none',
              fontSize: '14px',
              flex: 1,
              background: 'transparent',
              height: '100%',
              padding: '0 14px',
            }}
            countrySelectorStyleProps={{
              buttonStyle: {
                border: 'none',
                borderRight: '1px solid #eaecf0',
                background: 'transparent',
                height: '100%',
                padding: '0 10px',
                borderRadius: '8px 0 0 8px',
              },
            }}
          />
          {verificationError && <p className="mt-1 text-xs text-red-500">{verificationError}</p>}
        </div>

        {/* Step 2: copy Bonnie number — revealed after Next */}
        {phoneSubmitted && (
          <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm animate-in fade-in duration-200 dark:border-gray-700 dark:bg-gray-900">
            <p className="text-base font-semibold text-gray-900 dark:text-gray-100">Copy Bonnie&apos;s phone number</p>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">You will need it during the onboarding.
            </p>
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Bonnie phone number
              </label>
              <div className={`mt-1 flex items-center rounded-md border ${
                !bonnieCopied
                  ? 'border-blue-500 ring-1 ring-blue-500 dark:border-blue-400 dark:ring-blue-400'
                  : 'border-gray-300 dark:border-gray-600'
              }`}>
                <span className="flex-1 px-3 py-2 font-mono text-sm text-gray-900 dark:text-gray-100">
                  {bonnieNumber ?? '—'}
                </span>
                <button
                  type="button"
                  onClick={copyBonnieNumber}
                  disabled={!bonnieNumber}
                  className={`flex items-center gap-1 rounded-r-md border-l px-3 py-2 text-xs disabled:opacity-50 ${
                    bonnieCopied
                      ? 'border-green-200 bg-green-50 text-green-700 dark:border-green-900/50 dark:bg-green-900/20 dark:text-green-400'
                      : 'border-gray-300 bg-gray-50 text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                  }`}
                  aria-label={bonnieCopied ? 'Bonnie number copied' : 'Copy Bonnie number'}
                >
                  {bonnieCopied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                  {bonnieCopied ? 'Copied' : 'Copy'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

        {/* Actions row — below both cards, right-aligned */}
        <div className="mt-4 flex justify-end">
          {!phoneSubmitted ? (
            <button
              type="button"
              onClick={submitPhone}
              disabled={!nationalDigits}
              className="inline-flex items-center gap-2 rounded-lg bg-gray-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-black disabled:opacity-40 dark:bg-gray-100 dark:text-gray-900 dark:hover:bg-white"
            >
              Next
              <ArrowUpRight className="h-4 w-4" />
            </button>
          ) : (
            <button
              type="button"
              onClick={handleConnect}
              disabled={!sdkLoaded || !metaConfigId || connecting || !isDesktop}
              className="inline-flex items-center gap-2 rounded-lg bg-gray-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-black disabled:opacity-40 dark:bg-gray-100 dark:text-gray-900 dark:hover:bg-white"
            >
              {connecting ? 'Connecting…' : (
                <>Connect Bonnie to WhatsApp <ArrowUpRight className="h-4 w-4" /></>
              )}
            </button>
          )}
        </div>
      </div>
    <div className="order-first lg:order-none">{sideCard}</div>
    </div>
    </>
  )
}
